import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { propEq, findIndex, append, set, lensProp } from 'ramda';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';

import Button from '../../../components/Button';
import { Root } from '../../../theme/globalStyles';

import Onboarding from '../../../components/Onboarding/components';
import Profile from '../../Profile/components';
import BusinessInfo from '../../BusinessInfo/components';
import PriceTable from '../../PriceTable/components';
import Services from '../../Services/components';
import EmployeesRegistration from '../../EmployeesRegistration/components';
import EmployeesList from '../../EmployeesList/components';
import Receipt from '../../Operational/Receipt/components';
import Cashier from '../../Operational/Cashier/components';
import PaymentMethods from '../../PaymentMethods/components';

import { upsertUser } from '../../Profile/store/thunk';
import { upsertEstablishment } from '../../BusinessInfo/store/thunk';
import { clearEstablishmentErrors } from '../../BusinessInfo/store/actions';
import { upsertEmployee } from '../../EmployeesRegistration/store/thunk';
import { upsertConfigurations } from '../../Operational/store/thunk';

import { loadUsersSituation } from '../../../components/Filter/store/thunk';

import { setIsEditButtonClicked } from '../../EmployeesList/store/actions';
import { setAppPermissionEstablishments } from '../../Login/store/actions';
import { setIsOnboardingOpen, setIsFromRegistration } from '../../../components/Onboarding/store/actions';
import { setToken } from '../../../services';

import { validations as basicValidations } from '../../Profile/components/validations';
import { validations as businessValidations } from '../../BusinessInfo/components/validations';
import { validations as employeesValidations } from '../../EmployeesRegistration/components/validations';

import { onboardingSteps } from '../../../components/Onboarding/components/resources';

import { useScreenMeasure } from '../../../helpers/hooks/useScreenMeasure';
import { checkedValueByType } from '../../../helpers/common';
import { convertFormattedMoneyToNumber } from '../../../helpers/converters';

import { useStyles } from './styles';

export default function InitialRegistration() {
  const dispatch = useDispatch();

  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const isProduction = process.env.REACT_APP_IS_JUMP_PRODUCTION == 'true';
  const isResale = !!process.env.REACT_APP_RESALE_ID;
  const resaleId = process.env.REACT_APP_RESALE_ID || '';

  const isFromLogin = history?.location?.state?.data?.isFromLogin;

  const pathSegments = location.pathname.split('/');
  const isCadastroInicial = pathSegments.includes('cadastro-inicial');
  const isProfileRegistration = location.pathname === url;
  const isEstablishmentRegistration = isCadastroInicial && pathSegments.includes('estabelecimento');
  const isPriceTableRegistration = isCadastroInicial && pathSegments.includes('tabela-de-precos');
  const isServiceRegistration = isCadastroInicial && pathSegments.includes('servicos');
  const isUserRegistration = isCadastroInicial && pathSegments.includes('cadastro-de-usuario');

  const [isMobile] = useScreenMeasure();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { token, loggedIn } = useSelector(state => state.login);
  const { isOnboardingOpen } = useSelector(state => state.onboarding);

  const profileSubmit = useSelector(state => state.profile.submit);
  const profileLoading = useSelector(state => state.profile.isLoading);
  const businessInfoSubmit = useSelector(state => state.businessInfo.submit);
  const businessLoading = useSelector(state => state.businessInfo.isLoading);
  const services = useSelector(state => state.services.list);
  const employeesSubmit = useSelector(state => state.employeesRegistration.submit);
  const isEmployeesEditButtonClicked = useSelector(state => state.employeesList.isEditButtonClicked);
  const pathLogo = useSelector(state => state.operational.receipt.pathLogo);

  const isAdminUser = profileSubmit?.isAdminUser;
  const isLoading = profileLoading || businessLoading;

  const initialConfig = [
    {
      id: 'profile',
      title: 'Dados do usuário',
      path: `${url}`,
      isSkippable: false,
      component: Profile,
      onSubmit: params => {
        delete params['emailConfirm'];

        if(isResale) {
          params['resaleId'] = resaleId;
        }

        return dispatch(upsertUser({ ...params, ...(resaleId && { resaleId }) }))
      },
      validations: basicValidations,
      submitParams: {
        userId,
        ...profileSubmit
      }
    },
    {
      id: 'businessInfo',
      title: 'Dados do estabelecimento',
      path: `${url}/estabelecimento`,
      isSkippable: false,
      component: BusinessInfo,
      onSubmit: params => {
        setBusinessInfoData(params);

        if(isResale) {
          params['isInitialRegistration'] = true;
          params['resaleId'] = resaleId;
        }

        dispatch(clearEstablishmentErrors());
        return dispatch(upsertEstablishment({ ...params, ...(resaleId && { resaleId }) }));
      },
      validations: businessValidations,
      submitParams: {
        userId,
        establishmentId,
        ...businessInfoSubmit
      }
    }
  ];

  const additionalConfig = [
    {
      id: 'user-registration',
      title: isEmployeesEditButtonClicked ? 'Edição de usuário' : 'Cadastro de usuário',
      path: `${url}/cadastro-de-usuario`,
      isSkippable: true,
      component: EmployeesRegistration,
      onSubmit: params => {
        const { employeeId } = params || {};

        const newParams = {
          ...params,
          mainUser: 0
        }

        delete newParams['userId'];
        delete newParams['establishmentId'];
        delete newParams['employeeId'];

        dispatch(setIsEditButtonClicked(false));
        return dispatch(upsertEmployee(userId, employeeId, newParams, true));
      },
      validations: employeesValidations,
      submitParams: {
        userId,
        ...employeesSubmit
      }
    },
    {
      id: 'user-list',
      title: 'Listagem de usuários',
      path: `${url}/usuarios`,
      isSkippable: true,
      component: EmployeesList,
      onSubmit: 'push',
      submitParams: {}
    },
    {
      id: 'receipt',
      title: 'Comprovantes',
      path: `${url}/comprovantes`,
      isSkippable: true,
      component: Receipt,
      onSubmit: params => {
        const {
          userId,
          establishmentId,
          print,
          printNote
        } = params || {};

        const formattedParams = {
          pathLogo,
          print: print || 2,
          printNote: printNote || '',
          page: 'Comprovantes'
        }

        return dispatch(upsertConfigurations(userId, establishmentId, formattedParams));
      },
      submitParams: {
        userId,
        establishmentId
      }
    },
    {
      id: 'cashier',
      title: 'Caixa',
      path: `${url}/caixa`,
      isSkippable: true,
      component: Cashier,
      onSubmit: params => {
        const {
          userId,
          establishmentId,
          closeCashierKeepMoney,
          requiredOpenedCashierForVehicleEntry,
          cashReserve,
          withdrawal
        } = params || {};

        const formattedParams = {
          closeCashierKeepMoney: checkedValueByType(closeCashierKeepMoney, 'number'),
          requiredOpenedCashierForVehicleEntry: checkedValueByType(requiredOpenedCashierForVehicleEntry, 'number'),
          cashReserve: convertFormattedMoneyToNumber(cashReserve),
          withdrawal: withdrawal || 1,
          page: 'Caixa'
        }

        if(closeCashierKeepMoney) {
          delete formattedParams['cashReserve'];
        }

        return dispatch(upsertConfigurations(userId, establishmentId, formattedParams));
      },
      submitParams: {
        userId,
        establishmentId
      }
    },
    {
      id: 'payment-methods',
      title: 'Meios de pagamento',
      path: `${url}/meios-de-pagamento`,
      isSkippable: true,
      component: PaymentMethods,
      onSubmit: 'push',
      submitParams: {}
    }
  ];

  const [isReloadFinished, setIsReloadFinished] = useState(false);

  const [businessInfoData, setBusinessInfoData] = useState({});
  const [config, setConfig] = useState(initialConfig);
  const [nextStep, handleStepNavigation] = useState(0);

  const currentIndex = findIndex(propEq('path', location.pathname), config);
  const currentStep = config[currentIndex];

  const servicesPath = {
    id: 'services',
    title: 'Serviços',
    path: `${url}/servicos`,
    isSkippable: false,
    component: Services,
    onSubmit: 'push',
    submitParams: {}
  }

  const priceTablePath = {
    id: 'priceTable',
    title: 'Tabela de preços',
    path: `${url}/tabela-de-precos`,
    isSkippable: false,
    component: PriceTable,
    onSubmit: 'push',
    submitParams: {}
  }

  useEffect(() => {
    dispatch(setIsOnboardingOpen(true));
  }, [currentStep]);

  useEffect(() => {
    if(isProfileRegistration && !loggedIn) {
      dispatch(setIsOnboardingOpen(true));
    }
  }, [isProfileRegistration, loggedIn]);

  useEffect(() => {
    if(loggedIn || isFromLogin) {
      history.push(config[nextStep + 1].path);
    }
  }, [isFromLogin, loggedIn]);

  useEffect(() => {
    handleStepNavigation(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if(token) {
      setToken(token);
    }
  }, [token]);

  const handleEstablishmentTypes = establishmentTypes => {
    let newConfig = initialConfig;

    if(establishmentTypes.includes(1) || establishmentTypes.includes('1')) {
      newConfig = append(priceTablePath, newConfig);
    }

    if(establishmentTypes.includes(2) || establishmentTypes.includes('2')) {
      newConfig = append(servicesPath, newConfig);
    }

    newConfig = [
      ...newConfig,
      ...additionalConfig
    ];

    setConfig(newConfig);
  }

  useEffect(() => {
    const isPageReloaded = window.performance
      .getEntriesByType('navigation')
      .map((nav) => nav.type)
      .includes('reload');

    window.scrollTo(0, 0);

    if(isPageReloaded && !isReloadFinished) {
      setIsReloadFinished(true);
      return;
    }

    if(config[nextStep]) {
      history.push(config[nextStep].path);
    }
  }, [config, nextStep]);

  const handleNextStep = values => {
    dispatch(setIsFromRegistration(true));

    const { onSubmit, id } = currentStep;

    let params = {
      ...values,
      userId: userId ?? null,
      establishmentId: establishmentId ?? null
    }

    if(id == 'priceTable' || id == 'services') {
      dispatch(loadUsersSituation(userId, token)).then(data => {
        dispatch(setAppPermissionEstablishments(data?.establishments, isAdminUser));
      });
    }

    if(
      id == 'priceTable' &&
      businessInfoSubmit?.establishmentTypes.includes(1) &&
      businessInfoSubmit?.establishmentTypes.includes(2)
    ) {
      handleStepNavigation(nextStep + 1);
    }

    if(id == 'services' && services?.length == 0) {
      return toast.error('Você precisa cadastrar pelo menos um serviço.');
    }

    if(onSubmit == 'push') {
      if(id == 'priceTable') {
        return handleStepNavigation(nextStep + 1);
      }

      if(id == 'services') {
        return handleStepNavigation(nextStep + 1);
      }

      if(id == 'user-list') {
        return handleStepNavigation(nextStep + 1);
      }

      if(id == 'payment-methods') {
        return history.push('/visao-geral');
      }

      return handleStepNavigation('next');
    }

    if(id == 'businessInfo') {
      const { establishmentTypes } = params;

      handleEstablishmentTypes(establishmentTypes);
      params = set(lensProp('userId'), userId, params);
    }

    return onSubmit(params)
      .then(() => handleStepNavigation(nextStep + 1));
  }

  const handlePreviousStep = async () => {
    if(isUserRegistration && isEmployeesEditButtonClicked) {
      return handleStepNavigation(nextStep + 1);
    }

    if(!nextStep) {
      history.push('/');
      return;
    }

    handleStepNavigation(nextStep - 1);
  }

  return(
    <Root>
      <Container className={classes.root}>
        {(isProduction && isOnboardingOpen && currentStep?.id == 'profile') && (
          <Onboarding
            step={onboardingSteps.WELCOME}
            buttonTitle='Iniciar cadastro'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'businessInfo') && (
          <Onboarding
            step={onboardingSteps.ESTABLISHMENT}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'priceTable') && (
          <Onboarding
            step={onboardingSteps.PRICE_TABLE}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'services') && (
          <Onboarding
            step={onboardingSteps.SERVICE_TABLE}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'user-registration') && (
          <Onboarding
            step={onboardingSteps.USER}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'receipt') && (
          <Onboarding
            step={onboardingSteps.TICKET}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'cashier') && (
          <Onboarding
            step={onboardingSteps.CASHIER}
            buttonTitle='Avançar'
          />
        )}
        {(isProduction && isOnboardingOpen && currentStep?.id == 'payment-methods') && (
          <Onboarding
            step={onboardingSteps.PAYMENT_METHODS}
            buttonTitle='Avançar'
          />
        )}
        <Grid container direction="column" justify="space-between">
          {!isMobile && (
            <Stepper
              alternativeLabel
              nonLinear
              activeStep={currentIndex}
              style={{ background: 'transparent' }}
            >
              {config.map((nav, i) => (
                <Step key={nav.path}>
                  <StepButton
                    completed={currentIndex > i}
                    disabled={currentIndex < i}
                    component={Link}
                    to={nav.path}
                    style={{ textDecoration: 'none' }}
                  >
                    {nav.title}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}
          <Grid>
            {config.map(({ component: Component, ...nav }) => (
              <Route
                exact
                key={nav.path}
                path={nav.path}
                render={routeProps => (
                  <Formik
                    enableReinitialize
                    initialValues={nav.id == 'businessInfo' ? businessInfoData : nav.submitParams}
                    validationSchema={nav.validations}
                    onSubmit={handleNextStep}
                  >
                    {({ ...formikProps }) => (
                      <Form>
                        <Component
                          formikProps={formikProps}
                          handlePreviousStep={handlePreviousStep}
                          {...routeProps}
                        />
                        <Grid
                          className={classes.footer}
                          style={{ position: 'fixed', bottom: 0 }}
                        >
                          <Container>
                            <Grid
                              container
                              justify={(!isEstablishmentRegistration && !isPriceTableRegistration && !isServiceRegistration && (isUserRegistration && isEmployeesEditButtonClicked)) ? "space-between" : "flex-end"}
                            >
                              {(!isEstablishmentRegistration && !isPriceTableRegistration && !isServiceRegistration && (isUserRegistration && isEmployeesEditButtonClicked)) && (
                                <Button
                                  type="button"
                                  color="secondary"
                                  loading={isLoading}
                                  onClick={handlePreviousStep}
                                >
                                  Voltar
                                </Button>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                                {(currentStep?.isSkippable) && (
                                  <Button color="primary" onClick={() => history.push('/visao-geral')}>
                                    Encerrar primeiros passos
                                  </Button>
                                )}
                                {isUserRegistration && (
                                  <Button color="primary" onClick={() => handleStepNavigation(nextStep + 1)}>
                                    Cadastrar depois
                                  </Button>
                                )}
                                <Button color="success" loading={isLoading}>
                                  Avançar
                                </Button>
                              </div>
                            </Grid>
                          </Container>
                        </Grid>
                      </Form>
                    )}
                  </Formik>
                )}
              />
            ))}
          </Grid>
        </Grid>
      </Container>
    </Root>
  );
}